import { codeHash } from '../../../session/testResults'

// What the Code Coach substudy's chat tells the server about the student's work, with every new
// question, card answer and checker retry (ccTutorService.js): the code as it is now, and the
// latest run on this page - its results, how each test ended, the exception it raised and the
// end of its terminal output - with the hash of the code that run tested. The server offers both
// to the AI roles as evidence (code:<codeHash>, run:<runId>), so a run's results are never read
// as the current code's once the student has edited it, and a checker that answers after a card
// can tell the code at the question from the code now.
//
//   { code, codeHash, run: null | { runId, codeHash, results, outcomes, errorName, terminalTail } }
//
// run is null until the student runs the tests on this page: TerminalContext keeps no run across
// a reload, and nothing older is sent as if it were current.

// The terminal's end, as much as the server takes.
export const TERMINAL_TAIL_MAX = 2000

// The terminal's lines as the student sees them (Terminal.jsx shows each line's content), joined,
// and cut to the last `max` characters.
export const terminalTail = (terminalOutput, max = TERMINAL_TAIL_MAX) => {
  const text = (Array.isArray(terminalOutput) ? terminalOutput : [])
    .map((line) => (line?.content == null ? '' : String(line.content)))
    .join('\n')
  return text.length > max ? text.slice(-max) : text
}

// From TerminalContext: lastRunInfo ({ runId, codeHash, errorName, ... } of the latest finished
// run), testResults and testOutcomes (that run's, per test), terminalOutput and isRunning.
export const buildCcContext = ({ code, lastRunInfo, testResults, testOutcomes, terminalOutput, isRunning = false }) => {
  const text = typeof code === 'string' ? code : String(code ?? '')
  const runId = lastRunInfo && typeof lastRunInfo === 'object' ? lastRunInfo.runId : null
  const run =
    runId != null && runId !== ''
      ? {
          runId: String(runId),
          codeHash: lastRunInfo.codeHash ? String(lastRunInfo.codeHash) : null,
          results: (Array.isArray(testResults) ? testResults : []).map((passed) => passed === true),
          outcomes: (Array.isArray(testOutcomes) ? testOutcomes : []).map((outcome) => ({
            status: typeof outcome?.status === 'string' ? outcome.status : null,
            errorType: outcome?.errorType ? String(outcome.errorType) : null,
            message: outcome?.message ? String(outcome.message) : null,
          })),
          errorName: lastRunInfo.errorName ? String(lastRunInfo.errorName) : null,
          // A run in progress has emptied the terminal for its own output, which is not this
          // run's: rather nothing than another run's lines under this one's id.
          terminalTail: isRunning ? '' : terminalTail(terminalOutput),
        }
      : null
  return { code: text, codeHash: codeHash(text), run }
}
